import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../../i18n/LanguageContext';
import Section from '../../components/ui/Section';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { sendEmail } from '../../utils/emailService';
import { config } from '../../utils/config';
import { getActiveServices as getActiveServicesApi, createServiceRequest as createServiceRequestApi } from '../../utils/servicesAPI';
import { Helmet } from 'react-helmet';

const PageContainer = styled.div`
  padding-top: 70px;
`;

const HeroSection = styled.div`
  background-image: url('https://www.dailymood.it/wp-content/uploads/2018/04/home-35.jpg');
  background-size: cover;
  background-position: center;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const HeroContent = styled.div`
  position: relative;
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: white;
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #111;
  color: #fff;
  width: 100%;
  max-width: 760px;
  border: 1px solid #333;
  border-radius: 16px;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  position: relative;
  height: 220px;
  background-size: cover;
  background-position: center;
`;
  
const ModalHeaderOverlay = styled.div`
    position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0.4), rgba(0,0,0,0.75));
`;

const ModalTitle = styled.h3`
  position: absolute;
  bottom: 1rem;
  left: 1.5rem;
  margin: 0;
  font-size: 1.75rem;
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: #a3a3a3;
`;

const Input = styled.input`
  background: #0b0b0b;
  border: 1px solid #333;
  color: #fff;
  border-radius: 10px;
  padding: 0.9rem 1rem;
`;

const TextArea = styled.textarea`
  background: #0b0b0b;
  border: 1px solid #333;
  color: #fff;
  border-radius: 10px;
  padding: 0.9rem 1rem;
  min-height: 110px;
  resize: vertical;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  background: rgba(255,255,255,0.08);
  color: #fff;
  border: 1px solid #333;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
`;

const Experiences: React.FC = () => {
  const { language } = useLanguage();
  const [selectedExperience, setSelectedExperience] = React.useState<any | null>(null);
  const [clientName, setClientName] = React.useState('');
  const [clientEmail, setClientEmail] = React.useState('');
  const [clientPhone, setClientPhone] = React.useState('');
  const [requestedDate, setRequestedDate] = React.useState('');
  const [requestedTime, setRequestedTime] = React.useState('');
  const [participants, setParticipants] = React.useState(1);
  const [message, setMessage] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [submitSuccess, setSubmitSuccess] = React.useState<string | null>(null);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const handleOpen = (exp: any) => {
    setSelectedExperience(exp);
    setSubmitSuccess(null);
    setSubmitError(null);
  };

  const handleClose = () => {
    setSelectedExperience(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedExperience) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      // 1) Prova a mappare su un servizio reale per salvataggio in DB e visibilità admin
      const category: string | undefined = selectedExperience.category;
      let mappedServiceId: string | null = null;
      if (category) {
        const { success, services } = await getActiveServicesApi(category);
        if (success && services && services.length > 0) {
          // prova match su titolo
          const expTitle = (language === 'it' ? selectedExperience.it : selectedExperience.en).toLowerCase();
          const exact = services.find(s => (s.title_it || s.title_en).toLowerCase().includes(expTitle.split(' ')[0]));
          mappedServiceId = (exact || services[0]).id;
        }
      }

      if (mappedServiceId) {
        const result = await createServiceRequestApi({
          serviceId: mappedServiceId,
          clientName,
          clientEmail,
          clientPhone,
          requestedDate,
          requestedTime,
          preferredTimeSlots: undefined,
          participantsCount: participants,
          message
        });
        if (!result.success) throw new Error(result.error || 'Errore creazione richiesta');
      } else {
        // 2) Fallback: invia almeno email all'admin
        const subject = `Richiesta esperienza: ${language === 'it' ? selectedExperience.it : selectedExperience.en}`;
        const html = `
          <h2>Nuova richiesta esperienza</h2>
          <p><strong>Esperienza:</strong> ${language === 'it' ? selectedExperience.it : selectedExperience.en}</p>
          <p><strong>Nome:</strong> ${clientName}</p>
          <p><strong>Email:</strong> ${clientEmail}</p>
          <p><strong>Telefono:</strong> ${clientPhone || '-'}</p>
          <p><strong>Data:</strong> ${requestedDate || '-'}</p>
          <p><strong>Orario:</strong> ${requestedTime || '-'}</p>
          <p><strong>Partecipanti:</strong> ${participants}</p>
          <p><strong>Messaggio:</strong><br/>${message || '-'}</p>
        `;
        const text = `Nuova richiesta esperienza: ${(language === 'it' ? selectedExperience.it : selectedExperience.en)}\nNome: ${clientName}\nEmail: ${clientEmail}\nTelefono: ${clientPhone}\nData: ${requestedDate}\nOrario: ${requestedTime}\nPartecipanti: ${participants}\nMessaggio: ${message}`;
        const { success, error } = await sendEmail(config.app.supportEmail, { subject, html, text });
        if (!success) throw new Error(error || 'Errore invio email');
      }

      setSubmitSuccess(language === 'it' ? 'Richiesta inviata! Ti contatteremo a breve.' : 'Request sent! We will contact you soon.');
      setClientName(''); setClientEmail(''); setClientPhone(''); setRequestedDate(''); setRequestedTime(''); setParticipants(1); setMessage('');
    } catch (err: any) {
      setSubmitError(err.message || 'Errore imprevisto');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <PageContainer>
      <Helmet>
        <title>
          {language === 'it' 
            ? 'Servizi/Experience | J-O-H' 
            : 'Services/Experience | J-O-H'}
        </title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Esperienze personalizzate per arricchire il tuo soggiorno: tour enogastronomici, corsi di cucina, escursioni private e molto altro.' 
            : 'Personalized experiences to enrich your stay: food and wine tours, cooking classes, private excursions, and much more.'} 
        />
      </Helmet>
      
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            {language === 'it' ? 'Servizi/Experience' : 'Services/Experience'}
          </HeroTitle>
          <HeroSubtitle>
            {language === 'it'
              ? 'Esperienze uniche e personalizzate per rendere il tuo soggiorno indimenticabile.'
              : 'Unique and personalized experiences to make your stay unforgettable.'}
          </HeroSubtitle>
          <Button variant="primary" to="/contact">
            {language === 'it' ? 'Richiedi Informazioni' : 'Request Information'}
          </Button>
        </HeroContent>
      </HeroSection>
      
      <Section padding="4rem 0">
        <Content>
          <CardsGrid>
            {[
              {
                it: 'Tour Enogastronomici',
                en: 'Food & Wine Tours',
                category: 'culture',
                subtitleIt: '3–5 ore • piccoli gruppi',
                subtitleEn: '3–5 hours • small groups',
                descriptionIt: 'Visite guidate a cantine selezionate con degustazioni e abbinamenti locali.',
                descriptionEn: 'Guided visits to selected wineries with tastings and local pairings.',
                tags: ['Degustazioni', 'Cantine', 'Locale'],
                price: 'da €69/pp',
                image: 'https://www.gecoconsulenzealberghiere.com/wp-content/uploads/2021/08/turismo-enogastronomico-formazione-tour-operating-geco-consulenze-alberghiere.jpg'
              },
              {
                it: 'Corsi di Cucina',
                en: 'Cooking Classes',
                category: 'courses',
                subtitleIt: '2–4 ore • chef professionisti',
                subtitleEn: '2–4 hours • professional chefs',
                descriptionIt: 'Impara ricette tradizionali con ingredienti freschi e tecniche autentiche.',
                descriptionEn: 'Learn traditional recipes with fresh ingredients and authentic techniques.',
                tags: ['Tradizione', 'Mani in pasta', 'Locale'],
                price: 'da €59/pp',
                image: 'https://images.unsplash.com/photo-1504754524776-8f4f37790ca0?q=80&w=1350&auto=format&fit=crop'
              },
              {
                it: 'Escursioni Private',
                en: 'Private Excursions',
                category: 'culture',
                subtitleIt: 'Mezza/Intera giornata • guida dedicata',
                subtitleEn: 'Half/Full day • private guide',
                descriptionIt: 'Tour su misura tra natura, borghi storici e città d’arte con guide multilingue.',
                descriptionEn: 'Tailored tours across nature, historic towns and art cities with multilingual guides.',
                tags: ['Natura', 'Cultura', 'Esclusivo'],
                price: 'su richiesta',
                image: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?q=80&w=1350&auto=format&fit=crop'
              },
              {
                it: 'Servizi di Concierge',
                en: 'Concierge Services',
                category: 'events',
                subtitleIt: 'H24 • personalizzazione totale',
                subtitleEn: '24/7 • fully personalized',
                descriptionIt: 'Transfer privati, prenotazioni, benessere in villa e assistenza dedicata.',
                descriptionEn: 'Private transfers, reservations, in-villa wellness and dedicated assistance.',
                tags: ['Transfer', 'Benessere', 'Lifestyle'],
                price: 'su richiesta',
                image: 'https://siviaggia.it/wp-content/uploads/sites/2/2016/08/concierge-in-hotel.jpg'
              },
              {
                it: 'Yoga & Wellness',
                en: 'Yoga & Wellness',
                category: 'wellness',
                subtitleIt: '60–90 min • lezioni private',
                subtitleEn: '60–90 min • private sessions',
                descriptionIt: 'Sessioni rigeneranti in location esclusive con istruttori certificati.',
                descriptionEn: 'Restorative sessions in exclusive locations with certified instructors.',
                tags: ['Benessere', 'Mindfulness', 'Equilibrio'],
                price: 'da €49/pp',
                image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=1350&auto=format&fit=crop'
              },
              {
                it: 'Lezioni di Italiano',
                en: 'Italian Lessons',
                category: 'courses',
                subtitleIt: '1–2 ore • insegnanti qualificati',
                subtitleEn: '1–2 hours • qualified teachers',
                descriptionIt: 'Percorsi linguistici su misura per ogni livello, anche intensivi.',
                descriptionEn: 'Tailored language paths for every level, including intensive formats.',
                tags: ['Lingua', 'Cultura', 'Personalizzato'],
                price: 'da €35/pp',
                image: 'https://images.unsplash.com/photo-1460518451285-97b6aa326961?q=80&w=1350&auto=format&fit=crop'
              }
            ].map((x, idx) => (
              <Card
                key={x.it}
                image={x.image}
                title={language === 'it' ? x.it : x.en}
                subtitle={language === 'it' ? x.subtitleIt : x.subtitleEn}
                description={language === 'it' ? x.descriptionIt : x.descriptionEn}
                tags={x.tags}
                price={x.price}
                onClick={() => handleOpen(x)}
                hoverEffect={true}
                delay={idx * 0.05}
              />
            ))}
          </CardsGrid>
        </Content>
      </Section>
      
      {selectedExperience && (
        <ModalOverlay onClick={handleClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative' }}>
              <ModalHeader style={{ backgroundImage: `url(${selectedExperience.image})` }}>
                <ModalHeaderOverlay />
                <ModalTitle>
                  {language === 'it' ? selectedExperience.it : selectedExperience.en}
                </ModalTitle>
                <CloseButton onClick={handleClose}>✕</CloseButton>
              </ModalHeader>
            </div>
            <ModalBody>
              <form onSubmit={handleSubmit}>
                <Row>
                  <Field>
                    <Label>{language === 'it' ? 'Nome completo' : 'Full name'}</Label>
                    <Input value={clientName} onChange={(e) => setClientName(e.target.value)} required />
                  </Field>
                  <Field>
                    <Label>Email</Label>
                    <Input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} required />
                  </Field>
                </Row>
                <Row>
                  <Field>
                    <Label>{language === 'it' ? 'Telefono' : 'Phone'}</Label>
                    <Input value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} />
                  </Field>
                  <Field>
                    <Label>{language === 'it' ? 'Partecipanti' : 'Participants'}</Label>
                    <Input type="number" min={1} value={participants} onChange={(e) => setParticipants(parseInt(e.target.value || '1', 10))} />
                  </Field>
                </Row>
                <Row>
                  <Field>
                    <Label>{language === 'it' ? 'Data preferita' : 'Preferred date'}</Label>
                    <Input type="date" value={requestedDate} onChange={(e) => setRequestedDate(e.target.value)} />
                  </Field>
                  <Field>
                    <Label>{language === 'it' ? 'Orario preferito' : 'Preferred time'}</Label>
                    <Input type="time" value={requestedTime} onChange={(e) => setRequestedTime(e.target.value)} />
                  </Field>
                </Row>
                <Field>
                  <Label>{language === 'it' ? 'Messaggio' : 'Message'}</Label>
                  <TextArea value={message} onChange={(e) => setMessage(e.target.value)} placeholder={language === 'it' ? 'Esigenze, domande o preferenze...' : 'Requirements, questions or preferences...'} />
                </Field>
                {submitError && (
                  <div style={{ color: '#ef4444', marginTop: '0.75rem' }}>{submitError}</div>
                )}
                {submitSuccess && (
                  <div style={{ color: '#004744', marginTop: '0.75rem' }}>{submitSuccess}</div>
                )}
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                  <Button type="submit" variant="primary" disabled={submitting}>
                    {submitting ? (language === 'it' ? 'Invio...' : 'Sending...') : (language === 'it' ? 'Invia Richiesta' : 'Send Request')}
                  </Button>
                  <Button variant="outline" onClick={handleClose}>
                    {language === 'it' ? 'Annulla' : 'Cancel'}
                  </Button>
                </div>
              </form>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
      
      
      
    </PageContainer>
  );
};

export default Experiences; 